import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getGuardAvailability } from 'api/canvms/incidents';
import type { IncidentDetail, DispatchIncidentInput, GuardAvailability } from '../../types/canvision.types';
import { fetchGuardLocations } from 'api/dispatch/dispatchApi';
import { GuardWithLocationResponse } from 'api/dispatch/types';

interface DispatchDialogProps {
  open: boolean;
  incident: IncidentDetail;
  onClose: () => void;
  onDispatch: (input: DispatchIncidentInput) => Promise<{ patrolId: number } | undefined>;
  onViewPatrol?: (patrolId: number) => void;
}

interface GuardOption {
  guardId: number;
  name: string;
  isOnDuty: boolean;
  distanceKm: number | null;
  currentAssignment: string | null;
  lastSeenMinutesAgo: number | null;
}

// Duration options in hours
const DURATION_OPTIONS = [
  { value: 0.5, label: '30 minutes' },
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: -1, label: 'Custom' },
];

// Start time options
type StartTimeOption = 'now' | 'asap' | 'custom';

/**
 * Calculate distance between two coordinates using Haversine formula
 */
function calculateDistanceKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function DispatchDialog({
  open,
  incident,
  onClose,
  onDispatch,
  onViewPatrol,
}: DispatchDialogProps) {
  // Guard selection state
  const [guards, setGuards] = useState<GuardOption[]>([]);
  const [guardsLoading, setGuardsLoading] = useState(false);
  const [guardsError, setGuardsError] = useState<string | null>(null);
  const [selectedGuardId, setSelectedGuardId] = useState<number | null>(null);
  const [searchFilter, setSearchFilter] = useState('');

  // Dispatch options state
  const [startTimeOption, setStartTimeOption] = useState<StartTimeOption>('now');
  const [customStartTime, setCustomStartTime] = useState('');
  const [durationOption, setDurationOption] = useState(1);
  const [customDuration, setCustomDuration] = useState('1');
  const [instructions, setInstructions] = useState('');
  const [notifySupervisor, setNotifySupervisor] = useState(true);

  // Dispatch state
  const [dispatching, setDispatching] = useState(false);
  const [dispatchError, setDispatchError] = useState<string | null>(null);
  const [dispatchSuccess, setDispatchSuccess] = useState<{ patrolId: number } | null>(null);

  // Guard availability state
  const [availability, setAvailability] = useState<GuardAvailability | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const availabilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Site coordinates (would come from incident/site data)
  // For now, we'll use placeholder coordinates since site coords may not be available
  const siteCoords = useMemo(() => {
    // TODO: Get actual site coordinates from incident data
    // For now return null to skip distance calculation
    return null as { lat: number; lon: number } | null;
  }, [incident]);

  // Load guards when dialog opens
  useEffect(() => {
    if (open) {
      loadGuards();
      // Reset state
      setSelectedGuardId(null);
      setSearchFilter('');
      setStartTimeOption('now');
      setCustomStartTime('');
      setDurationOption(1);
      setCustomDuration('1');
      setInstructions(`Investigate ${incident.incident_type.toLowerCase()} at ${incident.address_title || incident.site_display_name || incident.site_name || 'site'}.`);
      setNotifySupervisor(true);
      setDispatchError(null);
      setDispatchSuccess(null);
      setAvailability(null);
    }
  }, [open, incident]);

  const loadGuards = async () => {
    try {
      setGuardsLoading(true);
      setGuardsError(null);
      const response = await fetchGuardLocations({ activeOnly: false });

      const guardOptions: GuardOption[] = response.guards.map((guard) => {
        // Determine if on duty (has recent location ping)
        const isOnDuty = guard.minutesAgo !== null && guard.minutesAgo < 30;

        // Calculate distance from site
        let distanceKm: number | null = null;
        if (siteCoords && guard.latitude && guard.longitude) {
          distanceKm = calculateDistanceKm(
            siteCoords.lat,
            siteCoords.lon,
            guard.latitude,
            guard.longitude
          );
        }

        return {
          guardId: guard.guardId,
          name: guard.name,
          isOnDuty,
          distanceKm,
          currentAssignment: guard.currentPatrol?.siteName || null,
          lastSeenMinutesAgo: guard.minutesAgo,
        };
      });

      // Sort: on duty first, then by distance (if available), then by name
      guardOptions.sort((a, b) => {
        // On duty first
        if (a.isOnDuty !== b.isOnDuty) return a.isOnDuty ? -1 : 1;
        // Then by distance
        if (a.distanceKm !== null && b.distanceKm !== null) {
          return a.distanceKm - b.distanceKm;
        }
        if (a.distanceKm !== null) return -1;
        if (b.distanceKm !== null) return 1;
        // Then by name
        return a.name.localeCompare(b.name);
      });

      setGuards(guardOptions);
    } catch (err: any) {
      console.error('[CANVMS/DispatchDialog] Failed to load guards:', err);
      setGuardsError(err.message || 'Failed to load guards');
    } finally {
      setGuardsLoading(false);
    }
  };

  // Filter guards by search
  const filteredGuards = useMemo(() => {
    if (!searchFilter.trim()) return guards;
    const search = searchFilter.toLowerCase();
    return guards.filter((g) => g.name.toLowerCase().includes(search));
  }, [guards, searchFilter]);

  const selectedGuard = guards.find((g) => g.guardId === selectedGuardId);

  // Fetch guard availability when guard or dispatch params change
  const fetchAvailability = useCallback(async (guardId: number) => {
    try {
      setAvailabilityLoading(true);
      const startIso = startTimeOption === 'custom' && customStartTime
        ? new Date(customStartTime).toISOString()
        : new Date().toISOString();
      const date = startIso.slice(0, 10);
      const duration = durationOption === -1 ? (parseFloat(customDuration) || 1) : durationOption;

      const result = await getGuardAvailability(guardId, date, startIso, duration);
      setAvailability(result.data);
    } catch (err) {
      console.error('[CANVMS/DispatchDialog] Failed to fetch guard availability:', err);
      setAvailability(null);
    } finally {
      setAvailabilityLoading(false);
    }
  }, [startTimeOption, customStartTime, durationOption, customDuration]);

  useEffect(() => {
    if (!selectedGuardId) {
      setAvailability(null);
      return;
    }
    // Debounce availability fetch (300ms)
    if (availabilityTimerRef.current) clearTimeout(availabilityTimerRef.current);
    availabilityTimerRef.current = setTimeout(() => {
      fetchAvailability(selectedGuardId);
    }, 300);
    return () => {
      if (availabilityTimerRef.current) clearTimeout(availabilityTimerRef.current);
    };
  }, [selectedGuardId, fetchAvailability]);

  // Calculate actual start time based on option
  const getStartTime = (): string => {
    switch (startTimeOption) {
      case 'now':
        return new Date().toISOString();
      case 'asap':
        // ASAP means the guard will start when available
        // For the API, we still send current time but backend handles it
        return new Date().toISOString();
      case 'custom':
        if (customStartTime) {
          // Convert local datetime to ISO
          return new Date(customStartTime).toISOString();
        }
        return new Date().toISOString();
      default:
        return new Date().toISOString();
    }
  };

  // Get duration in hours
  const getDurationHours = (): number => {
    if (durationOption === -1) {
      return parseFloat(customDuration) || 1;
    }
    return durationOption;
  };

  const handleDispatch = async () => {
    if (!selectedGuardId) return;

    try {
      setDispatching(true);
      setDispatchError(null);

      const input: DispatchIncidentInput = {
        guardId: selectedGuardId,
        startTime: getStartTime(),
        durationHours: getDurationHours(),
        instructions: instructions.trim() || undefined,
        notifySupervisor,
      };

      const result = await onDispatch(input);

      if (result?.patrolId) {
        setDispatchSuccess(result);
      }
    } catch (err: any) {
      console.error('[CANVMS/DispatchDialog] Failed to dispatch:', err);
      setDispatchError(err.message || 'Failed to dispatch guard');
    } finally {
      setDispatching(false);
    }
  };

  const handleViewPatrol = () => {
    if (dispatchSuccess?.patrolId && onViewPatrol) {
      onViewPatrol(dispatchSuccess.patrolId);
    }
    onClose();
  };

  const handleClose = () => {
    if (!dispatching) {
      onClose();
    }
  };

  // Success view
  if (dispatchSuccess) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          Guard Dispatched Successfully
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" gutterBottom>
              {selectedGuard?.name || 'Guard'} has been dispatched to{' '}
              <strong>{incident.address_title || incident.site_display_name || incident.site_name}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Patrol ID: {dispatchSuccess.patrolId}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Duration: {getDurationHours()} hour(s)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          {onViewPatrol && (
            <Button onClick={handleViewPatrol} variant="outlined">
              View Patrol
            </Button>
          )}
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dispatch Guard to Incident</DialogTitle>
      <DialogContent dividers>
        {/* Incident Info Header */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Incident
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {incident.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Site: {incident.site_display_name || incident.site_name || 'Unknown'}
          </Typography>
          {incident.customer_name && (
            <Typography variant="body2" color="text.secondary">
              Customer: {incident.customer_name}
            </Typography>
          )}
          {incident.address_title && (
            <Typography variant="body2" color="text.secondary">
              Address: {incident.address_title}
            </Typography>
          )}
        </Box>

        {/* Guard Selection */}
        <Typography variant="subtitle2" gutterBottom>
          Select Guard
        </Typography>

        <TextField
          size="small"
          placeholder="Search guards..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          fullWidth
          sx={{ mb: 1 }}
        />

        {guardsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : guardsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {guardsError}
          </Alert>
        ) : (
          <List
            sx={{
              maxHeight: 200,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 2,
            }}
            dense
          >
            {filteredGuards.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No guards found"
                  secondary={searchFilter ? 'Try a different search' : undefined}
                />
              </ListItem>
            ) : (
              filteredGuards.map((guard) => (
                <ListItemButton
                  key={guard.guardId}
                  selected={selectedGuardId === guard.guardId}
                  onClick={() => setSelectedGuardId(guard.guardId)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Radio
                      checked={selectedGuardId === guard.guardId}
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color={guard.isOnDuty ? 'success' : 'disabled'} />
                        {guard.name}
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={guard.isOnDuty ? 'on duty' : 'off duty'}
                          size="small"
                          color={guard.isOnDuty ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.7rem' }}
                        />
                        {guard.distanceKm !== null && (
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOnIcon sx={{ fontSize: 14 }} />
                            {guard.distanceKm.toFixed(1)} km
                          </Box>
                        )}
                        {guard.currentAssignment && (
                          <Typography variant="caption" color="text.secondary">
                            @ {guard.currentAssignment}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              ))
            )}
          </List>
        )}

        {/* Guard Availability Summary */}
        {selectedGuardId && (
          <Box sx={{ mb: 1 }}>
            {availabilityLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Checking schedule...
                </Typography>
              </Box>
            ) : availability ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: availability.hasConflict ? 'error.50' : 'action.hover',
                  border: '1px solid',
                  borderColor: availability.hasConflict ? 'error.200' : 'success.light',
                }}
              >
                {availability.hasConflict ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <WarningAmberIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      <Typography variant="body2" fontWeight="medium" color="error.main">
                        Schedule conflict
                      </Typography>
                    </Box>
                    {availability.conflictingPatrols.map((cp, i) => (
                      <Typography key={i} variant="caption" color="text.secondary" display="block">
                        Overlaps with {cp.customerName} ({new Date(cp.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {new Date(cp.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})
                      </Typography>
                    ))}
                    {availability.nextAvailableWindow && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Next available: {new Date(availability.nextAvailableWindow.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    <Typography variant="body2" color="success.main">
                      {availability.patrols.length === 0
                        ? 'Available — no patrols scheduled'
                        : `Available — no schedule conflicts (${availability.patrols.length} other patrol${availability.patrols.length !== 1 ? 's' : ''} today)`
                      }
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : null}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Start Time */}
        <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
          <FormLabel component="legend">Start Time</FormLabel>
          <RadioGroup
            row
            value={startTimeOption}
            onChange={(e) => setStartTimeOption(e.target.value as StartTimeOption)}
          >
            <FormControlLabel value="now" control={<Radio size="small" />} label="Now" />
            <FormControlLabel value="asap" control={<Radio size="small" />} label="ASAP (when available)" />
            <FormControlLabel value="custom" control={<Radio size="small" />} label="Custom" />
          </RadioGroup>
          {startTimeOption === 'custom' && (
            <TextField
              type="datetime-local"
              size="small"
              value={customStartTime}
              onChange={(e) => setCustomStartTime(e.target.value)}
              sx={{ mt: 1, maxWidth: 250 }}
              inputProps={{
                min: new Date().toISOString().slice(0, 16),
              }}
            />
          )}
        </FormControl>

        {/* Duration */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Duration</InputLabel>
          <Select
            value={durationOption}
            label="Duration"
            onChange={(e) => setDurationOption(e.target.value as number)}
          >
            {DURATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {durationOption === -1 && (
          <TextField
            size="small"
            label="Custom Duration (hours)"
            type="number"
            value={customDuration}
            onChange={(e) => setCustomDuration(e.target.value)}
            inputProps={{ min: 0.5, max: 12, step: 0.5 }}
            sx={{ mb: 2 }}
            fullWidth
          />
        )}

        {/* Instructions */}
        <TextField
          label="Instructions for Guard"
          multiline
          rows={3}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          placeholder="Provide instructions for the guard..."
        />

        {/* Notify Supervisor */}
        <FormControlLabel
          control={
            <Checkbox
              checked={notifySupervisor}
              onChange={(e) => setNotifySupervisor(e.target.checked)}
            />
          }
          label="Notify supervisor"
        />

        {dispatchError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setDispatchError(null)}>
            {dispatchError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={dispatching}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleDispatch}
          disabled={dispatching || !selectedGuardId}
          startIcon={dispatching ? <CircularProgress size={16} /> : <LocalShippingIcon />}
        >
          {dispatching ? 'Dispatching...' : 'Dispatch Guard'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DispatchDialog;
